<template>
  <v-btn-toggle
    :model-value="modelValue"
    :color="color"
    base-color="control-surface"
    :density="density"
    :disabled="disabled"
    :aria-label="ariaLabel"
    variant="flat"
    mandatory="force"
    divided
    border
    class="self-start border-outline border-opacity-100"
    role="group"
    :data-cy="dataCy"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-btn
      v-for="item in items"
      :key="String(item.value)"
      variant="flat"
      :color="color"
      base-color="control-surface"
      :value="item.value"
      :disabled="item.disabled === true"
      :data-cy="item.dataCy"
    >
      {{ item.label }}
    </v-btn>
  </v-btn-toggle>
</template>

<script setup lang="ts">
/**
 * A segmented control for one exclusive choice out of two or three: the options
 * sit in a single bordered group, so they read as one question rather than as
 * separate buttons. Past three options, or when options need descriptions, use
 * LlSelect or LlRadioGroup instead.
 *
 * Inside a button group Vuetify applies `color` only while an option is selected
 * and `base-color` otherwise, which is what fills the chosen option brand green
 * and leaves the rest on the secondary-button grey. Both are bound per button as
 * well as on the group: VBtnGroup would hand them down through `provideDefaults`,
 * but that chain runs through the host app's own defaults, and a silently
 * unfilled button is indistinguishable from a broken one.
 *
 * `self-start` is load-bearing. `.v-btn-group` is `inline-flex`, but a flex item
 * is blockified to `flex`, and the settings rows nest this in a `flex flex-col`
 * card body — without it the group's border spans the whole column.
 *
 * `border-outline` is the control weight, not the softer `outline-variant` that
 * panels use: this is something you click.
 *
 * `mandatory="force"` keeps the setting answered — clicking the selected option
 * cannot clear it, and a value outside the option set emits the first one rather
 * than rendering a group with nothing lit.
 */
export type ButtonToggleValue = string | number | boolean;

export interface ButtonToggleItem {
  label: string;
  value: ButtonToggleValue;
  disabled?: boolean;
  dataCy?: string;
}

withDefaults(
  defineProps<{
    modelValue: ButtonToggleValue | null;
    items: ButtonToggleItem[];
    /** Names the group for screen readers, which otherwise announce only the options. */
    ariaLabel: string;
    color?: string;
    density?: 'default' | 'comfortable' | 'compact';
    disabled?: boolean;
    dataCy?: string;
  }>(),
  {
    color: 'primary',
    density: 'default',
    disabled: false,
    dataCy: '',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: ButtonToggleValue];
}>();
</script>
